<template>
  <div class="learning-dashboard">
    <div v-if="loading" class="flex flex-col items-center justify-center py-12" style="min-height: 300px;">
      <v-progress-circular indeterminate color="#21427d" :size="48" />
      <span class="mt-2 text-sm text-gray-500 font-medium">Loading learning catalog...</span>
    </div>

    <template v-else>
      <!-- View: Player -->
      <LessonPlayer
        v-if="currentView === 'player'"
        :course="selectedCourse"
        :initialLessonId="selectedLessonId"
        :currentUserId="currentUserId"
        @back="currentView = 'detail'"
      />

      <!-- View: Course Detail -->
      <CourseDetail
        v-else-if="currentView === 'detail' && selectedCourse"
        :course="selectedCourse"
        :currentUserId="currentUserId"
        @back="goToDashboard"
        @play-lesson="onPlayLesson"
      />

      <!-- View: Dashboard (default) -->
      <div v-else>
        <!-- Page Header -->
        <!-- Tabs: Courses / User Progress -->
        <ActitiveTabcomponent :tabs="learningTabs">
          <!-- Create Course Button dynamically in header slot -->
          <template #tab-buttons="{ activeTab }">
            <v-btn
              v-if="activeTab === 'courses'"
              color="#21427d"
              class="white--text normal-case rounded-lg px-5"
              depressed
              v-iam-disabled="'learning.courses.create'"
              @click="showCourseForm = true"
            >
              <v-icon left>mdi-plus</v-icon>Create Course
            </v-btn>
          </template>

          <!-- Courses Tab -->
          <template #courses>
            <div class="pt-4">
              <!-- Filters -->
              <div class="flex flex-wrap gap-3 mb-5 items-center">
                <v-text-field
                  v-model="searchQuery"
                  prepend-inner-icon="mdi-magnify"
                  label="Search courses..."
                  outlined
                  dense
                  hide-details
                  class="max-w-xs"
                  clearable
                />
                <v-select
                  v-model="filterCategory"
                  :items="categories"
                  label="Category"
                  outlined
                  dense
                  hide-details
                  clearable
                  class="max-w-[180px]"
                />
                <v-select
                  v-model="filterDifficulty"
                  :items="difficulties"
                  label="Difficulty"
                  outlined
                  dense
                  hide-details
                  clearable
                  class="max-w-[180px]"
                />
                <v-spacer />
                <v-btn-toggle v-model="viewMode" mandatory dense color="#21427d">
                  <v-btn value="grid" icon><v-icon>mdi-view-grid</v-icon></v-btn>
                  <v-btn value="list" icon><v-icon>mdi-view-list</v-icon></v-btn>
                </v-btn-toggle>
              </div>

              <!-- Course Grid View -->
              <div
                v-if="viewMode === 'grid'"
                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
              >
                <v-card
                  v-for="course in filteredCourses"
                  :key="course._id"
                  class="course-card rounded-lg flex flex-col"
                  outlined
                  @click="viewCourse(course)"
                >
                  <!-- Card Header Color Block -->
                  <div
                    class="course-card-banner py-4 px-5 flex flex-col justify-between"
                    :style="{ background: getThumbGradient(course), height: '110px', minHeight: '110px', maxHeight: '110px', flexShrink: 0 }"
                  >
                    <div class="flex items-center justify-between mb-2">
                      <v-chip x-small dark color="rgba(255,255,255,0.2)" class="font-semibold">
                        {{ course.category }}
                      </v-chip>
                      <v-chip
                        x-small
                        dark
                        :color="getDifficultyColor(course.difficulty)"
                        class="font-semibold"
                      >
                        {{ course.difficulty }}
                      </v-chip>
                    </div>
                    <h3 class="text-white text-lg font-bold line-clamp-1 leading-snug">
                      {{ course.title }}
                    </h3>
                  </div>

                  <!-- Card Body -->
                  <v-card-text class="pa-5 flex-1 flex flex-col justify-between">
                    <div>
                      <p class="text-sm text-gray-500 line-clamp-3 mb-4 leading-relaxed">
                        {{ course.description }}
                      </p>
                      <div class="flex justify-between items-center text-xs text-gray-400 mb-4">
                        <span
                          ><v-icon x-small class="mr-1">mdi-book-multiple</v-icon
                          >{{ course.lessons.length }} Lessons</span
                        >
                        <span
                          ><v-icon x-small class="mr-1">mdi-account-group</v-icon
                          >{{ course.enrolledUsers.length }} Enrolled</span
                        >
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </div>

              <!-- Course List View -->
              <div v-else class="course-list-view flex flex-col gap-3">
                <v-card
                  v-for="course in filteredCourses"
                  :key="course._id"
                  class="course-list-card rounded-lg pa-4 cursor-pointer"
                  outlined
                  @click="viewCourse(course)"
                >
                  <div class="flex items-center gap-4 flex-wrap md:flex-nowrap">
                    <div
                      class="list-thumb flex-shrink-0 rounded-lg w-12 h-12 flex items-center justify-center"
                      :style="{ background: getThumbGradient(course) }"
                    >
                      <v-icon dark>mdi-school</v-icon>
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 class="text-base font-bold text-gray-800 truncate">{{ course.title }}</h3>
                        <v-chip x-small outlined color="#21427d" class="font-semibold">{{
                          course.category
                        }}</v-chip>
                        <v-chip
                          x-small
                          dark
                          :color="getDifficultyColor(course.difficulty)"
                          class="font-semibold"
                          >{{ course.difficulty }}</v-chip
                        >
                      </div>
                      <p class="text-xs text-gray-500 line-clamp-1 mb-0">{{ course.description }}</p>
                    </div>
                    <div class="flex items-center gap-6 flex-shrink-0 text-xs text-gray-400">
                      <span
                        ><v-icon x-small class="mr-1">mdi-book-multiple</v-icon
                        >{{ course.lessons.length }} Lessons</span
                      >
                      <span
                        ><v-icon x-small class="mr-1">mdi-account-group</v-icon
                        >{{ course.enrolledUsers.length }} Enrolled</span
                      >
                    </div>
                  </div>
                </v-card>
              </div>
            </div>
          </template>

          <!-- User Progress Tab -->
          <template #userProgress>
            <div class="pt-4">
              <UserProgressTracker />
            </div>
          </template>
        </ActitiveTabcomponent>
      </div>

      <!-- Course Form Dialog -->
      <CourseFormDialog
        v-model="showCourseForm"
        :editData="editCourseData"
        @save="handleCourseSave"
      />
    </template>
  </div>
</template>

<script>
import {
  state,
  CATEGORIES,
  DIFFICULTIES,
  initLearningData,
  addCourse,
  updateCourse
} from './learningData'
import CourseFormDialog from './CourseFormDialog.vue'
import CourseDetail from './CourseDetail.vue'
import LessonPlayer from './LessonPlayer.vue'
import UserProgressTracker from './UserProgressTracker.vue'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'

export default {
  name: 'LearningDashboard',
  components: {
    CourseFormDialog,
    CourseDetail,
    LessonPlayer,
    UserProgressTracker,
    ActitiveTabcomponent
  },
  data() {
    return {
      loading: true,
      currentView: 'dashboard', // 'dashboard' | 'detail' | 'player'
      selectedCourseId: null,
      selectedLessonId: null,
      currentUserId: (() => {
        try {
          const user = JSON.parse(localStorage.getItem('user'))
          return user?._id || 'u1'
        } catch (e) {
          return 'u1'
        }
      })(),
      showCourseForm: false,
      editCourseData: null,
      searchQuery: '',
      filterCategory: null,
      filterDifficulty: null,
      viewMode: 'grid',
      categories: CATEGORIES,
      difficulties: DIFFICULTIES
    }
  },
  async created() {
    this.loading = true
    try {
      await initLearningData(this.$axios)
    } catch (err) {
      console.error(err)
    } finally {
      this.loading = false
    }
  },
  computed: {
    selectedCourse() {
      if (!this.selectedCourseId) return null
      return this.courses.find(c => c._id === this.selectedCourseId) || null
    },
    learningTabs() {
      return [
        {
          key: 'courses',
          label: 'Courses',
          icon: 'mdi-book-multiple',
          component: { render: (h) => h('div') },
          permissionKey: 'learning.courses.read'
        },
        {
          key: 'userProgress',
          label: 'User Progress',
          icon: 'mdi-account-check',
          component: UserProgressTracker,
          permissionKey: 'learning.user_progress.read'
        }
      ]
    },
    courses() {
      return state.courses
    },
    filteredCourses() {
      let result = [...this.courses]
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        result = result.filter(c =>
          c.title.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.instructor.toLowerCase().includes(q)
        )
      }
      if (this.filterCategory) {
        result = result.filter(c => c.category === this.filterCategory)
      }
      if (this.filterDifficulty) {
        result = result.filter(c => c.difficulty === this.filterDifficulty)
      }
      return result
    }
  },
  methods: {
    getDifficultyColor(difficulty) {
      const map = { Beginner: '#4caf50', Intermediate: '#ff9800', Advanced: '#f44336' }
      return map[difficulty] || '#9e9e9e'
    },
    getThumbGradient(course) {
      const gradients = {
        Compliance: 'linear-gradient(135deg, #1a237e 0%, #42a5f5 100%)',
        'Product Training': 'linear-gradient(135deg, #004d40 0%, #26a69a 100%)',
        'Soft Skills': 'linear-gradient(135deg, #4a148c 0%, #ab47bc 100%)',
        Technical: 'linear-gradient(135deg, #b71c1c 0%, #ef5350 100%)'
      }
      return gradients[course.category] || 'linear-gradient(135deg, #21427d 0%, #5e8fd6 100%)'
    },
    viewCourse(course) {
      this.selectedCourseId = course._id
      this.currentView = 'detail'
    },
    goToDashboard() {
      this.selectedCourseId = null
      this.selectedLessonId = null
      this.currentView = 'dashboard'
    },
    onPlayLesson(lesson) {
      this.selectedLessonId = lesson._id
      this.currentView = 'player'
    },
    async handleCourseSave(formData) {
      this.loading = true
      try {
        if (this.editCourseData) {
          await updateCourse(this.$axios, this.editCourseData._id, formData)
          this.editCourseData = null
        } else {
          await addCourse(this.$axios, formData)
        }
        await initLearningData(this.$axios)
      } catch (err) {
        console.error(err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}
.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: white;
  border-radius: 12px;
  border: 1px solid #eef2f7;
  transition: all 0.25s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.stat-card:hover {
  box-shadow: 0 4px 14px rgba(33, 66, 125, 0.1);
  transform: translateY(-2px);
}
.stat-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stat-value {
  font-size: 1.4rem;
  font-weight: 800;
  color: #21427d;
  line-height: 1.2;
}
.stat-label {
  font-size: 0.75rem;
  color: #9e9e9e;
  font-weight: 500;
}

/* Course Grid */
.course-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 20px;
}
.course-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #eef2f7;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.course-card:hover {
  box-shadow: 0 8px 25px rgba(33, 66, 125, 0.12);
  transform: translateY(-4px);
  border-color: #21427d;
}
.course-card-banner {
  height: 110px;
  min-height: 110px;
  flex-shrink: 0;
}
.card-thumb {
  height: 160px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.thumb-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.course-card:hover .thumb-overlay {
  opacity: 1;
}
.play-icon {
  transition: transform 0.3s ease;
}
.course-card:hover .play-icon {
  transform: scale(1.2);
}
.card-badges {
  position: absolute;
  top: 10px;
  right: 10px;
}
.card-category {
  position: absolute;
  bottom: 10px;
  left: 10px;
}
.card-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}
.card-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Course List View */
.course-list-item {
  cursor: pointer;
  transition: all 0.2s ease;
}
.course-list-item:hover {
  border-color: #21427d !important;
  box-shadow: 0 2px 12px rgba(33, 66, 125, 0.08);
}

/* Dark mode support */
.dark .stat-card {
  background: #1e1e2f;
  border-color: #2d2d44;
}
.dark .stat-value {
  color: #90caf9;
}
.dark .course-card {
  background: #1e1e2f;
  border-color: #2d2d44;
}
.dark .course-card:hover {
  border-color: #5e8fd6;
  box-shadow: 0 8px 25px rgba(94, 143, 214, 0.15);
}
.dark .course-list-item {
  background: #1e1e2f !important;
}
</style>
